import { SIZE_GUIDE_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import type { SizeGuideAPIResponse } from '@app/pages/api/size-guide/types';
import { getSafeUrl } from '@redbubble/utils/getSafeUrl';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { Gender, MeasurementUnit, SizeGuideData } from './types';
import { getMeasurementUnit } from './utils/dataHelpers';

export interface SizeGuideState {
  sizeGuideData: SizeGuideData | null;
  initialGender: Gender | null;
  initialMeasurementUnit: MeasurementUnit | null;
}

const EMPTY_STATE: SizeGuideState = {
  sizeGuideData: null,
  initialGender: null,
  initialMeasurementUnit: null,
};

interface SizeGuideContextValue {
  productTypeId: string;
  hasSizing: boolean;
  getSizeGuide: () => Promise<SizeGuideState>;
}

const SizeGuideContext = createContext<SizeGuideContextValue | null>(null);

export const useSizeGuide = (): SizeGuideContextValue => {
  const context = useContext(SizeGuideContext);
  if (!context) {
    throw new Error(
      'useSizeGuideContext must be used within a SizeGuideProvider'
    );
  }
  return context;
};

const fetchSizeGuide = async (variables: {
  productTypeId: string;
  workId: number;
  inventoryItemId: string;
  locale: string;
}): Promise<SizeGuideData | null> => {
  const url = getSafeUrl(SIZE_GUIDE_PUBLIC_ENDPOINT, window.location.href);

  if (!url) {
    throw new Error('Invalid URL for size guide endpoint');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(variables),
  });

  const jsonData: SizeGuideAPIResponse = await response.json();
  if (!response.ok) {
    throw new Error(
      jsonData && 'error' in jsonData
        ? jsonData.error
        : 'Failed to fetch size guide'
    );
  }
  if (jsonData && 'error' in jsonData) {
    throw new Error(jsonData.error);
  }
  return jsonData;
};

export interface SizeGuideProviderProps {
  productTypeId: string;
  workId: number;
  inventoryItemId: string;
  locale: string;
  currency: string;
  hasSizing: boolean;
  children: ReactNode;
}

/**
 * Size guide measurements are keyed by productTypeId and are only requested
 * on demand, when a visitor actually opens the size guide modal (it's a
 * supplementary, infrequently-used action — see DEAP-3908 / DEAP-6278).
 * Shared between every trigger on the page (SizeGuideButton,
 * SizeGuideTrustSignal) via context, with a per-productTypeId cache for the
 * lifetime of this PDP view so re-opening a style already seen this session
 * doesn't re-hit IHS.
 */
export const SizeGuideProvider = ({
  productTypeId,
  workId,
  inventoryItemId,
  locale,
  currency,
  hasSizing,
  children,
}: SizeGuideProviderProps) => {
  const cacheRef = useRef<Record<string, SizeGuideState>>({});

  const getSizeGuide = useCallback(async (): Promise<SizeGuideState> => {
    const cached = cacheRef.current[productTypeId];
    if (cached) return cached;

    if (!hasSizing) return EMPTY_STATE;

    try {
      const sizeGuideData = await fetchSizeGuide({
        productTypeId,
        workId,
        inventoryItemId,
        locale,
      });
      const result: SizeGuideState = {
        sizeGuideData,
        initialGender: sizeGuideData?.model?.male ? Gender.Male : Gender.Female,
        initialMeasurementUnit: getMeasurementUnit(currency),
      };
      cacheRef.current[productTypeId] = result;
      return result;
    } catch {
      return EMPTY_STATE;
    }
  }, [productTypeId, workId, inventoryItemId, locale, currency, hasSizing]);

  return (
    <SizeGuideContext.Provider
      value={{ productTypeId, hasSizing, getSizeGuide }}
    >
      {children}
    </SizeGuideContext.Provider>
  );
};
